import { DOCS_CONTEXT } from "./constants";

const STYLE_RULES = `OUTPUT FORMAT: Return only HTML (no markdown, no backticks, no preamble).
Use these styles:
- h2: <h2 style="color:#4285fb;border-bottom:2px solid #4285fb;padding-bottom:6px">
- h3: <h3 style="color:#333;margin-top:20px">
- Inline code: <code style="background:#e8f0ff;padding:2px 6px;border-radius:3px;font-size:0.9em;color:#4285fb">
- Code block: <pre style="background:#1e293b;color:#93c5fd;padding:16px;border-radius:6px;overflow:auto;font-size:0.85em"><code>
- Warning box: <div style="background:#fff8e1;border-left:4px solid #f0c040;padding:10px 14px;margin:10px 0;border-radius:0 4px 4px 0">
- Info box: <div style="background:#e8f0ff;border-left:4px solid #4285fb;padding:10px 14px;margin:10px 0;border-radius:0 4px 4px 0">
- Step box (for key numbered steps): <div style="background:#f0faf0;border:2px solid #43a047;border-radius:8px;padding:14px 16px;margin:12px 0"> with step number in a circle: <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#43a047;color:white;font-weight:700;font-size:14px;margin-right:10px">1</span> and step title in <strong style="font-size:1.05em">
- Table: <table style="width:100%;border-collapse:collapse"> with <th style="background:#4285fb;color:white;padding:8px;text-align:left"> and <td style="padding:8px;border-bottom:1px solid #eee">
- Never invent endpoint paths or field names.
- V2 DATE OF BIRTH: must be an object with string values: "dateOfBirth": {"year": "1950", "month": "01", "day": "01"} — NEVER a flat string like "1950-01-01".
- V2 GENDER: must be an object: "gender": {"gender": "MALE"} — valid values are MALE, FEMALE, OTHER, UNSPECIFIED (uppercase). NEVER use a flat string like "M".
- V2 ADDRESSES: must be an array: "addresses": [{...}] — NEVER a named object like {"address1": {...}}.
- V2 ADDRESS FIELDS: use "streetAddress" (single string like "35 CONN STREET", not separate streetName/streetNumber), "locality" (not "suburb"), "subdivision" (not "state"), "postalCode" (not "postcode"). Always include "addressType" (e.g. "RESIDENTIAL", "REGISTERED_OFFICE"). These V2 field names differ from V1.
- V2 AUTH: use header "api_key: your_key" (lowercase with underscore). NEVER use "Authorization: machine", "Authorization: Bearer", or "Api-Key".`;

export function buildSystemPrompt(): string {
  return [
    "You are a senior technical writer at FrankieOne producing accurate, polished HTML documentation for a customer PDF pack about the V2 API.",
    "",
    "FRANKIEONE DOCS CONTEXT:",
    DOCS_CONTEXT,
    "",
    STYLE_RULES,
  ].join("\n");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildPrompt(id: string, _clientName: string, mode: string, checks: Record<string, boolean>, _hasChildId: boolean, feedback: string): string {
  const modeText = mode === "api" ? "API-only integration" : mode === "portal" ? "Portal-only access" : "both Portal and API (recommended for testing)";
  const checkList = Object.entries(checks).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(", ");
  const childCtx = `CHILD ID INSTRUCTIONS: ALWAYS include X-Frankie-CustomerChildID in header examples and mentions, but wrap every child-ID-specific line or element in <span class="child-id-only"> so it can be toggled. For example in a header list: <span class="child-id-only">X-Frankie-CustomerChildID: your_child_id</span>. In prose mention child ID like: <span class="child-id-only">If your account uses a Child ID (multi-tenant), also include <code style="background:#e8f0ff;padding:2px 6px;border-radius:3px;font-size:0.9em;color:#4285fb">X-Frankie-CustomerChildID</code>.</span>`;
  const apiDocsCtx = `CONTENT TOGGLE INSTRUCTIONS — TWO CSS CLASSES:

1. class="api-docs-only" — content shown only in API and Both modes, HIDDEN in Portal-only mode.
   Use for: API endpoints, HTTP methods, request/response payloads, code snippets, auth header examples, API base URLs.

2. class="portal-docs-only" — content shown only in Portal and Both modes, HIDDEN in API-only mode.
   Use for: portal-specific UI instructions, portal navigation steps, portal screenshots.

WORKFLOW/TESTING SECTIONS: For KYC, IDV, KYB, and AML sections, you MUST create THREE separate workflow/testing sub-sections:
- h3 "... — via API" → wrap the ENTIRE section (h3 + all content) in <div class="api-docs-only">. Cover the full workflow using only API calls.
- h3 "... — via Portal" → wrap the ENTIRE section (h3 + all content) in <div class="portal-docs-only">. Cover the full workflow using only the portal UI.
- h3 "... — API + Portal Combined" → do NOT wrap. This is visible in all modes. Show the recommended approach using both API and portal together.

Each sub-section must be SELF-CONTAINED — a reader should be able to follow one path without needing the others. Use step boxes in all three.

For NON-workflow content (intros, reference tables, test personas), do NOT wrap unless it contains specific API endpoints or portal-only UI instructions. Use inline <span> wrappers for mixed content.`;
  const clientCtx = `Mode: ${modeText} | Active checks: ${checkList} | ${childCtx} | ${apiDocsCtx} | IMPORTANT: Do NOT reference any specific client or company name in the output. Write generically for "your integration" or "your account".`;
  const fb = feedback ? `\n\nUser feedback to incorporate: ${feedback}` : "";

  const map = {
    overview: `Write an "Overview & Getting Started" section for the FrankieOne V2 API. ${clientCtx}

Cover:
- What FrankieOne V2 is (identity verification and compliance platform — the current API version).
- Environments — present as TWO separate small tables (UAT and Production), each with two rows (API and Portal). Use word-break:break-all on URL cells.
  UAT Environment:
    API:    https://api.uat.frankie.one/v2/
    Portal: https://portal.uat.frankie.one/login
  Production Environment:
    API:    https://api.frankie.one/v2/
    Portal: https://portal.frankie.one/login
- Requesting credentials: contact your FrankieOne Presales consultant — they provision separate credentials for UAT and Production.
- Warning box: In UAT you test against sandboxes/data connector mocks with no cost. In Production, calls hit live data sources and incur costs per check. Always start with UAT.
- Health check: GET /v2/kyc/ruok confirms credentials are valid.
- Your first API call: create an individual via POST /v2/individuals using JAMES A TESTONE testbed persona. A 200 OK with an entityId confirms your credentials are valid.
- Do NOT include any curl or request examples — a pinned example is shown separately below.
- Do NOT mention portal access, portal navigation, or team management — those are covered in a dedicated Portal section.${fb}`,

    resources: `Write a "Resources & Support" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 400 words.

Cover:
- V2 API Documentation: link to https://docs.frankieone.com/docs/getting-started — the official reference for V2 API endpoints, guides, and examples.
- AI Assistant: the FrankieOne documentation site includes an AI assistant that can answer questions about API calls, integration patterns, and using FrankieOne in general.
- Direct Support Channels: set up a direct chat with the FrankieOne team via Microsoft Teams or Slack for quick questions and real-time support during integration. Ask your Presales consultant to arrange this.
- UAT Test Data: https://docs.frankieone.com/docs/uat-test-data — official testbed documentation.
- Predefined Workflows Reference: https://docs.frankieone.com/v2/api/developers/predefined-workflows — full list of available workflows.
- MCP Server: FrankieOne provides an MCP (Model Context Protocol) server for AI coding assistants. Details at https://docs.frankieone.com/v2/api/developers/frankie-one-mcp-server
- Postman Collection: FrankieOne provides a Postman collection for the V2 API — ask your Presales consultant for access.${fb}`,

    auth: `Write an "Authentication & Headers" section for the FrankieOne V2 API. ${clientCtx}

Cover:
- V2 uses the SAME authentication headers as V1.
- Header reference table:
  api_key (REQUIRED, all lowercase with underscore)
  X-Frankie-CustomerID (REQUIRED, uppercase ID at end — UUID format)
  X-Frankie-CustomerChildID (OPTIONAL, for multi-tenant setups)
  Content-Type: application/json (REQUIRED for all requests)
- Headers are CASE SENSITIVE. Emphasise correct casing.
- Show correct header block format:
  api_key: your_uat_api_key
  X-Frankie-CustomerID: your_customer_id
  Content-Type: application/json
- WARNING callout: api_key must be lowercase with underscore (not Api-Key, not API_KEY). CustomerID ends in uppercase ID (not Id).
- Python and Node.js code snippets showing correct headers. The example endpoint MUST be POST to https://api.uat.frankie.one/v2/individuals (create individual). The example payload MUST use the JAMES A TESTONE testbed persona with correct V2 format: {"individual": {"name": {"givenName": "JAMES", "middleName": "A", "familyName": "TESTONE"}, "dateOfBirth": {"year": "1950", "month": "01", "day": "01"}, "gender": {"gender": "MALE"}, "addresses": [{"addressType": "RESIDENTIAL", "streetAddress": "35 CONN STREET", "locality": "FERNTREE GULLY", "subdivision": "VIC", "postalCode": "3156", "country": "AUS"}]}}. Do NOT invent fake names, use flat string formats for DOB/gender, or use V1 address field names (state/suburb/postcode).
- 401 error troubleshooting table.
- Do NOT include a security best practices section.
- Do NOT include any curl or full request examples.
- Link to https://docs.frankieone.com/docs/reference/authentication${fb}`,

    portal: `Write a "Portal Access & Navigation" section for the FrankieOne V2 API. ${clientCtx}

Include ONLY these three points as simple paragraphs or bullet points. Do NOT use step boxes or numbered steps for this section — it is informational, not a process flow.
- Requesting portal access — contact the FrankieOne presales team.
  UAT: https://portal.uat.frankie.one/login
  Prod: https://portal.frankie.one/login
- Portal complements API testing — entities created via API appear in the portal immediately for visual confirmation. The portal also shows workflow execution history and results.
- Managing team access: once you have portal access, add team members by clicking the profile icon > Settings.
Do not add any other content.${fb}`,

    kyc: `Write a "Verifying Individuals (KYC)" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 1800 words. Use tables and bullet points. No curl examples.
IMPORTANT: Use "Step box" style for all workflow steps.

CRITICAL V2 FORMAT NOTES (apply to ALL examples):
- dateOfBirth MUST be an object: {"year": "1950", "month": "01", "day": "01"} — NOT a flat string.
- gender MUST be an object: {"gender": "MALE"} — valid: MALE, FEMALE, OTHER, UNSPECIFIED.
- addresses MUST be an array: [{...}]. Fields: streetAddress, locality, subdivision, postalCode, country, addressType.

Structure the section in this order:

FIRST — brief intro (NOT wrapped): KYC verifies an individual's identity. In V2, you create an individual then execute a workflow. Common workflows: AUS-Basic2V-TwoPlus (standard 2+2), AUS-Basic2V-TwoPlusID (2+2 + Gov ID).

THEN — h3 "KYC Workflow — via API" — wrap ENTIRE section in <div class="api-docs-only">. Step boxes:
Step 1 — Create Individual: POST /v2/individuals with name, DOB (year/month/day object), gender (object), addresses (array), and ID documents. Returns entityId.
Step 2 — Execute Workflow: POST /v2/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute. Or combine: POST /v2/individuals/new/serviceprofiles/{serviceName}/workflows/{workflowName}/execute.
Step 3 — Get Result: response contains workflowResult with status (PASS/FAIL/REVIEW), riskAssessment, workflowStepResults. For async, wait for webhook.
Step 4 — Remediation: update individual with corrected data, re-execute workflow.

THEN — h3 "KYC Workflow — via Portal" — wrap ENTIRE section in <div class="portal-docs-only">. Step boxes:
Step 1 — Create Individual: in the portal, click "Create Individual". Fill in name, DOB, address, ID documents. Save.
Step 2 — Execute Workflow: select a workflow from the dropdown (e.g. AUS-Basic2V-TwoPlus), click "Save & Verify".
Step 3 — View Results: the entity page shows workflow status, risk assessment, and individual step outcomes immediately.
Step 4 — Remediation: edit entity details in the portal, then click "Save & Verify" again.

THEN — h3 "KYC Workflow — API + Portal Combined" — do NOT wrap. Step boxes:
Step 1 — Create via API: POST /v2/individuals — entity appears in portal immediately.
Step 2 — Execute Workflow: via API or select workflow in portal and click "Save & Verify".
Step 3 — Review: check workflowResult via API and visually confirm in portal.
Step 4 — Remediate: fix details in portal, re-execute via API or portal.

THEN — h3 "Interpreting Workflow Results" (NOT wrapped — reference table):
  workflowResult.status — PASS, FAIL, REVIEW, CLEAR, ERROR/TIMEOUT.
  workflowResult.riskAssessment.riskLevel — LOW, MEDIUM, HIGH.
  workflowResult.workflowStepResults — array of step outcomes.
  workflowResult.issues — specific issues found.

THEN — h3 "Test It: KYC with Testbed Data" (NOT wrapped) — step boxes:
  Brief intro: only use official testbed names. Full data: https://docs.frankieone.com/docs/uat-test-data
Step 1 — Create JAMES A TESTONE using the reference below.
Step 2 — Execute AUS-Basic2V-TwoPlus.
Step 3 — Confirm PASS.
Step 4 — Test Failure: create individual with incorrect data, expect FAIL or REVIEW.

THEN — h3 "JAMES A TESTONE — Reference" (NOT wrapped — two-column table):
  givenName=JAMES, middleName=A, familyName=TESTONE, dateOfBirth={"year":"1950","month":"01","day":"01"}, gender={"gender":"MALE"}, Address: addressType=RESIDENTIAL, streetAddress="35 CONN STREET", locality=FERNTREE GULLY, subdivision=VIC, postalCode=3156, country=AUS, Driver Licence: country=AUS, region=VIC, idNumber=283229690.

Warning: NEVER use real customer data in UAT.${fb}`,

    idv: `Write an "Identity Document Verification (IDV)" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 1400 words. Use tables and bullet points. No curl examples.
IMPORTANT: Use "Step box" style for all workflow steps.

Structure the section in this order:

FIRST — brief intro (NOT wrapped): IDV verifies a physical identity document via photo capture and optionally matches the holder's face via biometrics. FrankieOne offers Hosted OneSDK (low-code) and Embedded OneSDK (full control).

THEN — h3 "IDV Delivery Methods" (NOT wrapped, NOT step boxes):
  Hosted OneSDK: generate onboarding URL, send to user. No SDK installation needed.
  Embedded OneSDK: install OneSDK in your app for full UI control.
  Hosted in Mobile (WebView): use WebView for document scanning + selfie within native apps.

THEN — h3 "Consent" (NOT wrapped): Required before capturing ID images. Built-in consent screen in OneSDK, or pass consent: true.

THEN — h3 "IDV Workflow — via API" — wrap ENTIRE section in <div class="api-docs-only">. Step boxes:
Step 1 — Create Individual: POST /v2/individuals to get an entityId.
Step 2 — Generate Onboarding URL: POST /idv/v2/idvalidate/onboarding-url with {"consent": true, "flowId": "your-flow-id"}. Optionally include entityId to link to existing individual.
Step 3 — User Completes Capture: user opens URL, photographs ID front/back, takes selfie.
Step 4 — OCR & Workflow: FrankieOne OCRs the document, populates entity, executes configured workflow automatically.
Step 5 — Get Results: GET the workflow execution result via API.

THEN — h3 "IDV Workflow — via Portal" — wrap ENTIRE section in <div class="portal-docs-only">. Step boxes:
Step 1 — Create Individual: click "Create Individual" in portal, fill in details.
Step 2 — Send IDV Link: from the entity page, generate and send the OneSDK onboarding URL.
Step 3 — User Completes Capture: user opens link, photographs ID, takes selfie.
Step 4 — Review Results: entity updates in portal with OCR data and workflow results. View captured ID images and outcomes directly.

THEN — h3 "IDV Workflow — API + Portal Combined" — do NOT wrap. Step boxes:
Step 1 — Create via API: POST /v2/individuals — entity appears in portal.
Step 2 — Generate URL: via API or from the portal entity page. Send to user.
Step 3 — User Captures: photographs ID and selfie via OneSDK.
Step 4 — Review: confirm results via API and visually in portal.

THEN — h3 "IDV Workflows" (NOT wrapped — table):
  AUS-Advanced1V-IDOnly — Enhanced ID + biometric verification.
  AUS-Advanced3V-TwoPlusID — Full verification + biometrics + enhanced checks.

THEN — h3 "Test It: IDV End-to-End" (NOT wrapped) — step boxes:
  Brief intro: a sample Australian driver's licence is included below.
Step 1 — Prepare a Fake ID: pick a testbed persona. Edit sample licence to place YOUR OWN PHOTO over the green square.
Step 2 — Generate & Send URL: create onboarding URL. Send link to your phone.
Step 3 — Capture on Phone: photograph fake ID front, back, selfie.
Step 4 — Observe Results: entity appears in portal with OCR data and workflow results.

Warning: NEVER use real customer documents in UAT.${fb}`,

    kyb: `Write a "Verifying Businesses (KYB)" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 1800 words. Use tables and bullet points. Australian KYB only. No curl examples.
IMPORTANT: Use "Step box" style for all workflow steps.

CRITICAL V2 FORMAT NOTES:
- Organization creation uses nested "details" and "addresses" structure.
- Address fields: streetAddress, locality, subdivision, postalCode, country, addressType (e.g. "REGISTERED_OFFICE").
- Addresses MUST be an array [{...}].

Structure the section in this order:

FIRST — brief intro (NOT wrapped): KYB verifies a business entity and its beneficial owners. In V2, create an organization and execute a KYB workflow (e.g. AUS-Organization-Ownership). Identifies directors and UBOs (>25% ownership).

THEN — h3 "KYB Workflow — via API" — wrap ENTIRE section in <div class="api-docs-only">. Step boxes:
Step 1 — Create Organization: POST /v2/organizations with body: {"organization": {"details": {"name": {"name": "...", "type": "REGISTERED"}, "entityType": "COMPANY"}, "addresses": [{"addressType": "REGISTERED_OFFICE", "country": "AUS"}]}}.
Step 2 — Execute KYB Workflow: POST /v2/organizations/{entityId}/serviceprofiles/{serviceName}/workflows/AUS-Organization-Ownership/execute. Async — returns 202, results via webhook.
Step 3 — Review UBO Results: GET execution results. Registry data is limited — typically names and roles only.
Step 4 — Run KYC on Each UBO: update each UBO with DOB, address, ID docs. Execute individual KYC workflows via POST /v2/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute.
Step 5 — Review: GET final results for all entities.

THEN — h3 "KYB Workflow — via Portal" — wrap ENTIRE section in <div class="portal-docs-only">. Step boxes:
Step 1 — Create Organisation: click "Create Organisation" in the portal. Search by ABN or ACN.
Step 2 — Run Ownership Lookup: the portal triggers the ASIC/ABR lookup. Wait for UBO results.
Step 3 — Complete UBO Details: for each UBO, click to edit and fill in missing details (DOB, address, ID docs).
Step 4 — Run KYC on Each UBO: select a workflow, click "Save & Verify" on each individual.
Step 5 — Run AML: select AML workflow on the organisation, click "Save & Verify".
Step 6 — Review: check all results on organisation and individual entity pages.

THEN — h3 "KYB Workflow — API + Portal Combined" — do NOT wrap. Step boxes:
Step 1 — Create via API: POST /v2/organizations — organisation appears in portal immediately.
Step 2 — Execute KYB via API: POST workflow execute — UBOs appear in portal as they're resolved.
Step 3 — Complete UBO Details in Portal: visually review and fill in missing information.
Step 4 — Run KYC: verify each UBO via API or portal.
Step 5 — Review: confirm all results in portal and via API.

THEN — h3 "What Data Comes Back from Registry" (NOT wrapped — warning + table):
  Warning: ASIC registry data typically provides only names and roles — NOT enough to run KYC.
  Table: Entity Category | Typical Data | Usually Missing
  UBOs | Name, ownership %, sometimes DOB | Address, ID docs
  Directors/Officers | Name, role, sometimes address | DOB, ID docs
  Direct Owners (orgs) | Company name, type, % | N/A

THEN — h3 "Test It: KYB with FOX PTY LTD" (NOT wrapped) — step boxes:
Step 1 — Create FOX PTY. LTD. (ACN: 052121347).
Step 2 — Execute AUS-Organization-Ownership. Wait for async completion.
Step 3 — Run KYC on Each UBO: both should PASS.
Step 4 — Run AML on the Organisation: should return CLEAR.

THEN — h3 "Additional Test Entities" (NOT wrapped — table):
  SIMPLE: FOX PTY. LTD. | ACN: 052121347 | 2 entities, all pass.
  MEDIUM: BCP (PITT STREET) PTY LTD | ACN: 650193865 | moderate structure.
  COMPLEX: CLARO DISABILITY SERVICES PTY LTD | ACN: 082554630 | large structure.

Warning: NEVER use real business data in UAT.
Ref: https://docs.frankieone.com/docs/kyb/managing-organizations${fb}`,

    aml: `Write an "AML, PEP & Sanctions Screening" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 1800 words. Use tables and bullet points. No curl examples.
IMPORTANT: Use "Step box" style for all workflow steps.

CRITICAL V2 FORMAT NOTE: dateOfBirth MUST be an object: {"year": "1990", "month": "01", "day": "01"} — NOT a flat string.

Structure the section in this order:

FIRST — brief intro (NOT wrapped): AML screening checks entities against PEP, sanctions, and adverse media watchlists. In V2, AML runs as part of a workflow — standalone (GLB-AML-Advanced, GLB-AML-Basic) or as a step within a KYC workflow.

THEN — h3 "AML Screening Types" (NOT wrapped, NOT step boxes — tables):
  Definitions: PEP = Politically Exposed Persons (Levels 1-4). Sanctions = OFAC, UN, EU, DFAT. Media = adverse news.
  Standalone: GLB-AML-Advanced (PEP + Sanctions + Media), GLB-AML-Basic (PEP + Sanctions only).
  Combined: most KYC workflows include AML. Do NOT reference non-existent workflow names.

THEN — h3 "AML Workflow — via API" — wrap ENTIRE section in <div class="api-docs-only">. Step boxes:
Step 1 — Screen: POST /v2/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/GLB-AML-Advanced/execute. Or use a combined KYC workflow.
Step 2 — Review Matches: check workflowResult.workflowStepResults for AML outcomes.
Step 3 — Resolve: via API, update match resolution status (False Positive, True Positive Accept/Reject).
Step 4 — Re-run: re-execute workflow. Status changes from REVIEW to PASS.

THEN — h3 "AML Workflow — via Portal" — wrap ENTIRE section in <div class="portal-docs-only">. Step boxes:
Step 1 — Screen: open entity in portal, select an AML workflow (e.g. GLB-AML-Advanced), click "Save & Verify".
Step 2 — Review Matches: on the entity page, view AML screening results and individual match details.
Step 3 — Resolve Each Match: click each match, set resolution status (False Positive, True Positive Accept/Reject).
Step 4 — Re-run: click "Save & Verify" again. Status changes from REVIEW to PASS.

THEN — h3 "AML Workflow — API + Portal Combined" — do NOT wrap. Step boxes:
Step 1 — Screen via API: execute AML workflow via API call.
Step 2 — Review in Portal: open entity to visually inspect match details.
Step 3 — Resolve in Portal: set resolution status on each match.
Step 4 — Re-run via API or Portal: re-execute to move status from REVIEW to PASS.

THEN — h3 "Resolution Statuses" (NOT wrapped — table):
  False Positive, True Positive Accept, True Positive Reject, Unknown Accept, Unknown Reject.

THEN — h3 "Ongoing Monitoring" (NOT wrapped):
  Configured at account level by implementations consultant. Monitored entities auto re-screened on watchlist updates. New matches trigger webhooks.

THEN — h3 "Test It: AML Scenarios" (NOT wrapped) — step boxes:
Scenario 1 — AML in Full Workflow: create DIANE NICHOLSON, execute AUS-Basic3V-TwoPlusID. Expect: KYC passes, AML flags.
Scenario 2 — AML-Only: create DIANE NICHOLSON, execute GLB-AML-Advanced. Expect: PEP, sanctions, media matches.
Scenario 3 — Clean: create JAMES A TESTONE (DOB: year=1950, month=01, day=01), execute GLB-AML-Basic. Expect: clear.
Scenario 4 — Resolve & Re-run: resolve as "False Positive", re-execute. Expect: PASS.

THEN — h3 "AML Test Personas" (NOT wrapped — table):
  DIANE NICHOLSON — PEP + Sanctions + Media. JAMES G TESTELEVEN — PEP only. BRUCE TESTNINETEEN — Sanctions only. STACY K TESTTWENTY — PEP + Sanctions.

THEN — h3 "DIANE NICHOLSON — Reference" (NOT wrapped — two-column table):
  givenName=DIANE, familyName=NICHOLSON, dateOfBirth={"year":"1990","month":"01","day":"01"}, gender={"gender":"OTHER"}, Address: addressType=RESIDENTIAL, streetAddress="710 Collins Street", locality=Docklands, subdivision=VIC, postalCode=3008, country=AUS, Driver Licence: country=AUS, region=VIC, idNumber=11111111.

Warning: NEVER use real customer data in UAT.${fb}`,

    risk: `Write a "Risk Attributes & Key-Value Pairs (KVPs)" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 1000 words. Use tables and code blocks. Include JSON examples.
IMPORTANT: Use "Step box" style only for the implementation steps. Reference content uses regular h3 sections.

Structure the section in this order:

FIRST — brief intro: You can pass custom risk attributes to FrankieOne entities using extraData — an array of key-value pairs (KVPs) on the entity object. These KVPs are stored on the entity and returned in API responses and webhook payloads, allowing you to attach and retrieve custom risk metadata.

THEN — h3 "KVP Structure" (NOT step boxes — code block + explanation):
Each KVP entry has three fields:
- kvpKey: a namespaced string key (e.g. "Risk.Level", "Risk.Bracket", "Info.Confidence")
- kvpValue: always a string, even for numbers/booleans (e.g. "1", "true", "LOW")
- kvpType: optional type hint — "general.string", "general.integer", "general.bool", "id.external", "result.id"
Show this JSON example:
{ "kvpKey": "Risk.Level", "kvpValue": "LOW", "kvpType": "general.string" }

THEN — h3 "Passing KVPs in V2" with step boxes:
Step 1 — Include extraData on Create/Update: when creating or updating an entity (POST /v2/individuals or PUT), include extraData array on the entity object. Show this JSON example inline:
{
  "entity": {
    "extraData": [
      { "kvpKey": "Risk.Level", "kvpValue": "LOW", "kvpType": "general.string" },
      { "kvpKey": "Risk.Bracket", "kvpValue": "1", "kvpType": "general.integer" },
      { "kvpKey": "Important.VERY_HIGH_RISK", "kvpValue": "true", "kvpType": "general.bool" }
    ]
  }
}
Step 2 — Retrieve KVPs: the same extraData array is returned in API responses (GET /v2/individuals/{entityId}) and in webhook payloads.
Step 3 — Search by KVP: use kvpType "id.external" for values you want to be searchable via search APIs (e.g. external reference IDs).

THEN — h3 "Recommended Key Patterns" (NOT step boxes — table):
  Action.Recommended — recommended disposition (PASS, FAIL, PASS_MANUAL) — general.string
  Risk.Level — textual risk level (LOW, HIGH, UNACCEPTABLE) — general.string
  Risk.Bracket — numeric bracket (1, 2, 3, 4) — general.integer
  Confidence.Level — textual confidence (HIGH, LOW) — general.string
  Risk.ResultID — reference ID for risk result — result.id

Show a realistic example KVP array:
[
  { "kvpKey": "Action.Recommended", "kvpValue": "PASS_MANUAL", "kvpType": "general.string" },
  { "kvpKey": "Risk.Level", "kvpValue": "LOW", "kvpType": "general.string" },
  { "kvpKey": "Risk.Bracket", "kvpValue": "1", "kvpType": "general.integer" },
  { "kvpKey": "Confidence.Level", "kvpValue": "HIGH", "kvpType": "general.string" },
  { "kvpKey": "Risk.ResultID", "kvpValue": "fadb41a6-3783-5635-22ee-5432a5568d78", "kvpType": "result.id" }
]

THEN — h3 "Best Practices" (NOT step boxes — bullet list):
- Namespace keys to avoid collisions (e.g. Risk.*, Info.*, Custom.MyProduct.*).
- Keep kvpValue as a string; use kvpType for semantic typing.
- Use id.external for values you want searchable via search APIs.
- Maintain a central dictionary of KVP keys your integration uses.
- Avoid storing sensitive PII in KVPs unless proper encryption and retention policies are applied.
- KVPs are returned in webhook payloads — your webhook consumer should expect extraData[] on the entity object.

Ref: https://docs.frankieone.com/docs/key-value-pairs${fb}`,

    sso: `Write an "SSO (SAML) Setup for FrankieOne Portal" section. ${clientCtx}

IMPORTANT: Keep output under 1200 words. Use tables, bullet points, and step boxes.
This is a portal feature — the same for V1 and V2. FrankieOne Portal supports SAML 2.0 (IdP-initiated only).

Structure the section in this order:

FIRST — brief intro: FrankieOne Portal supports SAML 2.0 IdP-initiated SSO. You configure your Identity Provider (Okta, Azure AD, Google Workspace, OneLogin, etc.) to connect to FrankieOne's SP. Only IdP-initiated SSO is supported — users must start from the IdP to access the portal via SSO.

THEN — h3 "Prerequisites" (NOT step boxes — bullet list):
- Portal SP metadata (provided by FrankieOne for your environment)
- An IdP with admin access to create/configure a SAML application
- Contact FrankieOne to request SP metadata and to share your IdP metadata back

THEN — h3 "Setup Steps" with step boxes:
Step 1 — Request SP Metadata: contact FrankieOne — they generate SP XML metadata for your environment.
Step 2 — Configure IdP Application: create a SAML 2.0 application in your IdP using the SP metadata. Set Single Sign-On URL to the AssertionConsumerService Location from SP metadata. Set Audience URI (SP Entity ID) to the EntityDescriptor entityID from metadata.
Step 3 — Map Attributes: set NameID format to EmailAddress. Add mandatory attributes as required by FrankieOne (including a "roles" attribute — comma-separated values in double quotes if multiple roles). FrankieOne docs specify three mandatory attributes in the Okta example.
Step 4 — Share IdP Metadata: download your IdP metadata (XML file or metadata URL) and send it to FrankieOne so they can complete SP-side configuration.
Step 5 — Test SSO: use IdP-initiated SSO to access the portal. Verify users arrive in portal and roles map correctly.

THEN — h3 "Okta Configuration" (NOT step boxes — numbered list):
1. Okta Admin → Applications → Create App Integration → SAML 2.0 → Next.
2. General Settings: name the app → Next.
3. Configure SAML: Single sign-on URL from SP metadata ACS. Audience URL from SP metadata Entity ID. Name ID format: EmailAddress. Application username: Okta username.
4. Add mandatory Attribute Statements as per FrankieOne guide.
5. Finish → get Okta metadata link/file → share with FrankieOne.

THEN — h3 "Azure AD Configuration" (NOT step boxes — numbered list):
1. Azure AD → Enterprise Applications → New application → Non-gallery / SAML-based app.
2. Upload or enter SP metadata (ACS URL, Entity ID).
3. Configure User Attributes & Claims: NameID = user.mail or user.userprincipalname. Add custom claims for roles as FrankieOne requires.
4. Download IdP metadata → send to FrankieOne.

THEN — h3 "Required Attribute Mapping" (NOT step boxes — table):
  Attribute | Value | Notes
  NameID | EmailAddress (user email) | Required — primary identifier
  roles | Comma-separated role values | Required — use double quotes if multiple roles
  (Other mandatory attributes as specified in FrankieOne Okta guide)

Warning box (CRITICAL): For roles to be auto-assigned via SSO, the role value passed from your IdP must be an EXACT string match to the role name configured in FrankieOne — including capitalisation, special characters, spaces, and quotation marks. For example, if the FrankieOne role is "Compliance Admin", your IdP must send exactly "Compliance Admin" — not "compliance admin", "COMPLIANCE ADMIN", or "ComplianceAdmin". Mismatched role strings will result in users being provisioned without the correct permissions.

THEN — h3 "Testing & Troubleshooting" (NOT step boxes — bullet list):
- Use SAML debugging tools (SAML Tracer browser extension, IdP test buttons) to inspect assertions.
- Common issues: time skew between IdP and SP (sync clocks), missing attributes or NameID format mismatch, metadata mismatches (Entity ID, ACS URL).
- If you see errors or need SP metadata again, contact FrankieOne support.
- Warning box: Only IdP-initiated SSO is supported. SP-initiated login (starting from the FrankieOne portal login page) is not available.${fb}`,

    webhooks: `Write a "Webhooks & Notifications" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 800 words. Use tables and bullet points. No curl examples.
IMPORTANT: Use "Step box" style ONLY for the 3 core webhook integration steps. Reference sections use regular content.

Structure the section in this order:

FIRST — brief intro: Webhooks send real-time notifications when workflows complete (especially async operations like KYB) and when monitoring alerts fire (new AML matches).

THEN — the 3 step boxes:
Step 1 — Configure Your Webhook Endpoint: work with your FrankieOne implementations consultant to register your HTTPS endpoint URL. Multiple endpoints are supported. No self-service API for webhook config.
Step 2 — Verify the Payload & Acknowledge: when a webhook arrives, verify the JWT signature using your customer-specific RSA public key (provided by consultant). Respond with 200 or 202 to acknowledge. If you don't acknowledge, FrankieOne retries with exponential backoff.
Step 3 — Process the Result: the webhook payload includes entityId, entityType, serviceName, workflowName, status, and result. Use these to fetch the full workflow execution result via the API.

THEN — h3 "Webhook Payload Fields" (NOT step boxes — table):
  apiVersion, entityId, entityCustomerReference, entityType (individual/organization), serviceName, workflowName, status, result.

THEN — h3 "Event Types" (NOT step boxes — table):
  Workflow Completed — verification finished, process outcome (PASS/REVIEW/FAIL).
  Monitoring Alert — AML monitoring detected a change (entity added to sanctions list, etc.).
  Risk Changed — entity risk level was updated.

THEN — h3 "JWT Authentication" (NOT step boxes):
  All webhook payloads are signed using JWT with RSA encryption. Validate the JWT in the Authorization: Bearer header. Contact your implementations consultant for your public key.

THEN — h3 "Retry & Dead Letter Queue" (NOT step boxes):
  Failed deliveries retry with exponential backoff — up to 50 attempts over 24 hours. After exhaustion, notification moves to DLQ. Contact support to retrieve failed notifications.

Ref: https://docs.frankieone.com/docs/reference/webhooks${fb}`,

    int_diagrams: `Write an "Integration Diagrams" section for the FrankieOne V2 API. ${clientCtx}

IMPORTANT: Keep output under 600 words. Create actual HTML/CSS diagrams — NOT mermaid syntax.

Create 2 clear architecture diagrams using HTML/CSS boxes and arrows:

(1) High-Level Integration Architecture:
Show how the customer's backend connects to FrankieOne:
  Your Backend -> FrankieOne V2 API -> Data Sources (credit bureaus, DVS, AML lists)
  FrankieOne V2 API -> Webhook -> Your Backend (async results)
  FrankieOne Portal (used alongside API for testing and manual operations)
  Style: coloured boxes for each system, labelled arrows showing data flow direction.

(2) Integration Options Overview:
Show the three ways to integrate with FrankieOne:
  Direct API — your backend calls V2 REST endpoints directly, executes workflows.
  Hosted OneSDK — FrankieOne-hosted UI for IDV/onboarding, your frontend loads the SDK, results come via webhook.
  Portal — manual entity creation, workflow execution, and review via the web portal.
  Style: side-by-side boxes showing each option with a brief description underneath.

Use print-friendly colours. Boxes should have borders and background fills.${fb}`,

    diagrams: `Write a "Sequence Diagrams" section for the FrankieOne V2 API. ${clientCtx}

Include 4 fully rendered HTML/CSS sequence diagrams (NOT mermaid — actual HTML with actor boxes and arrows):
(1) KYC: POST /v2/individuals -> POST execute workflow -> workflowResult (PASS/FAIL/REVIEW)
(2) KYB: POST /v2/organizations -> POST execute AUS-Organization-Ownership -> 202 -> webhook -> GET result -> KYC on UBOs
(3) IDV: POST /idv/v2/idvalidate/onboarding-url -> user capture -> OCR + auto workflow -> webhook -> GET result
(4) AML: POST execute AML workflow -> workflowResult with matches -> resolve -> re-execute
Style: coloured actor boxes at top, vertical lifelines, labelled horizontal arrows. Print-friendly.${fb}`,
  };

  return (map as Record<string, string>)[id] || `Write the ${id} section for the FrankieOne V2 API PDF pack. ${clientCtx}${fb}`;
}
